/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Await } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { Link } from '@/components/link';
import LevelAccess from '@/components/level-access';
import { cn } from '@/lib/utils';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import SupportPhoneNumber from '@/components/support-phone-number';
import FooterLinks from './elements/footer-links';
import NewsletterSignup from './elements/newsletter-signup';
import UtilityLinks from './elements/utility-links';

export const NEWSLETTER_SIGNUP_HEADING_CONTENT_ASSET_ID = 'footer-newsletter-signup-heading';

interface FooterProps {
    categories?: Promise<ShopperProducts.schemas['Category']>;
    /**
     * Resolves to the newsletter heading content asset, or `null` when the content asset is
     * unavailable (unpublished, missing, or SCAPI failure). The loader in `src/routes/_app.tsx`
     * is responsible for catching errors and resolving to `null` so the footer always renders.
     */
    newsletterSignupHeading: Promise<ContentAssetBody | null>;
}

const footerBottomLinkClassName =
    'inline-flex items-center gap-2 text-xs leading-4 text-footer-foreground underline-offset-4 transition-colors hover:underline focus-visible:underline';

export default function Footer({ newsletterSignupHeading }: FooterProps): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <footer className="mt-auto border-t border-footer-foreground/25 bg-footer-background text-footer-foreground">
            <div className="section-container flex flex-col gap-12 py-6 lg:gap-20 lg:py-8 lg:pt-20">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-6">
                    <FooterLinks className="lg:flex-1" />

                    <div className="flex w-full flex-col gap-4 lg:w-[30%] lg:shrink-0 lg:gap-6">
                        <Suspense fallback={<NewsletterSignup headingContent={null} />}>
                            <Await
                                resolve={newsletterSignupHeading}
                                errorElement={<NewsletterSignup headingContent={null} />}>
                                {(headingContent) => <NewsletterSignup headingContent={headingContent} />}
                            </Await>
                        </Suspense>
                        <UtilityLinks />
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 border-t border-footer-foreground/25 pt-4 text-footer-foreground lg:pt-6">
                    <div className="contents lg:flex lg:flex-wrap lg:items-center lg:gap-4">
                        <SupportPhoneNumber />

                        <p className="text-xs leading-4 text-footer-foreground">{t('footerBottom.copyright')}</p>

                        <Link
                            to="/sitemap"
                            className={cn(footerBottomLinkClassName, 'font-semibold uppercase tracking-button')}>
                            {t('footerBottom.sitemap')}
                        </Link>
                    </div>

                    <LevelAccess className="text-footer-foreground" svgClassName="h-7 w-auto" />
                </div>
            </div>
        </footer>
    );
}
